import { getIsMobile } from '../../../utils/getIsMobile';
import { type MDSAliases } from '../../../utils/mds/getMdsUrl';
import { Box } from '../../layout/Box';
import { Flex } from '../../layout/Flex';
import { PostMediaCover } from '../PostCard/styles';
import { ContentUI } from '../types';
import { DEFAULT_POST_COLLECTION_RATIO } from './constants';
import { CollagePreviewImage } from './parts/CollectionPreviewImage';
import { CollectionCollageProps } from './typings';

const generateGrid = (content: ContentUI[], imageAlias?: MDSAliases) => {
    if (content.length === 1) {
        return (
            <Box>
                <CollagePreviewImage preview={content[0]} imageAlias={imageAlias} />
            </Box>
        );
    }

    if (content.length === 2) {
        const [firstImage, secondImage] = content;

        return (
            <Flex
                backgroundColor="var(--background-secondary)"
                direction="row"
                flexWrap="wrap"
                spacing={1 / 8}
            >
                <Flex
                    width="50%"
                    height="100%"
                    flexBasis="calc(50% - 0.5px)"
                    alignItems="center"
                    justifyContent="center"
                    backgroundColor="var(--background-primary)"
                >
                    <CollagePreviewImage
                        preview={firstImage}
                        imageAlias={imageAlias}
                        width="100%"
                        height="100%"
                    />
                </Flex>
                <Flex
                    width="50%"
                    height="100%"
                    flexBasis="calc(50% - 0.5px)"
                    alignItems="center"
                    justifyContent="center"
                    backgroundColor="var(--background-primary)"
                >
                    <CollagePreviewImage
                        preview={secondImage}
                        imageAlias={imageAlias}
                        width="100%"
                        height="100%"
                    />
                </Flex>
            </Flex>
        );
    }

    if (content.length === 3) {
        const [firstImage, secondImage, thirdImage] = content;

        return (
            <Flex
                backgroundColor="var(--background-secondary)"
                direction="row"
                flexWrap="wrap"
                spacing={1 / 8}
            >
                <Flex
                    width="50%"
                    height="100%"
                    flexBasis="calc(50% - 0.5px)"
                    alignItems="center"
                    justifyContent="center"
                    backgroundColor="var(--background-primary)"
                >
                    <CollagePreviewImage
                        preview={firstImage}
                        imageAlias={imageAlias}
                        width="100%"
                        height="100%"
                    />
                </Flex>
                <Flex
                    width="50%"
                    height="100%"
                    direction="column"
                    flexWrap="wrap"
                    spacing={1 / 8}
                    flexBasis="calc(50% - 0.5px)"
                >
                    <Flex
                        width="100%"
                        height="calc(50% - 0.5px)"
                        flexBasis="calc(50% - 0.5px)"
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="var(--background-primary)"
                    >
                        <CollagePreviewImage
                            preview={secondImage}
                            imageAlias={imageAlias}
                            width="100%"
                            height="100%"
                        />
                    </Flex>
                    <Flex
                        width="100%"
                        height="calc(50% - 0.5px)"
                        flexBasis="calc(50% - 0.5px)"
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="var(--background-primary)"
                    >
                        <CollagePreviewImage
                            preview={thirdImage}
                            imageAlias={imageAlias}
                            width="100%"
                            height="100%"
                        />
                    </Flex>
                </Flex>
            </Flex>
        );
    }

    if (content.length > 3) {
        const [firstImage, secondImage, thirdImage, fourthImage] = content;

        return (
            <Flex
                backgroundColor="var(--background-secondary)"
                direction="row"
                flexWrap="wrap"
                spacing={1 / 8}
            >
                <Flex
                    width="50%"
                    height="100%"
                    direction="column"
                    flexWrap="wrap"
                    spacing={1 / 8}
                    flexBasis="calc(50% - 0.5px)"
                >
                    <Flex
                        width="100%"
                        height="calc(50% - 0.5px)"
                        flexBasis="calc(50% - 0.5px)"
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="var(--background-primary)"
                    >
                        <CollagePreviewImage
                            preview={firstImage}
                            imageAlias={imageAlias}
                            width="100%"
                            height="100%"
                        />
                    </Flex>
                    <Flex
                        width="100%"
                        height="calc(50% - 0.5px)"
                        flexBasis="calc(50% - 0.5px)"
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="var(--background-primary)"
                    >
                        <CollagePreviewImage
                            preview={thirdImage}
                            imageAlias={imageAlias}
                            width="100%"
                            height="100%"
                        />
                    </Flex>
                </Flex>
                <Flex
                    width="50%"
                    height="100%"
                    direction="column"
                    flexWrap="wrap"
                    spacing={1 / 8}
                    flexBasis="calc(50% - 0.5px)"
                >
                    <Flex
                        width="100%"
                        height="calc(50% - 0.5px)"
                        flexBasis="calc(50% - 0.5px)"
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="var(--background-primary)"
                    >
                        <CollagePreviewImage
                            preview={secondImage}
                            imageAlias={imageAlias}
                            width="100%"
                            height="100%"
                        />
                    </Flex>
                    <Flex
                        width="100%"
                        height="calc(50% - 0.5px)"
                        flexBasis="calc(50% - 0.5px)"
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="var(--background-primary)"
                    >
                        <CollagePreviewImage
                            preview={fourthImage}
                            imageAlias={imageAlias}
                            width="100%"
                            height="100%"
                        />
                    </Flex>
                </Flex>
            </Flex>
        );
    }

    return null;
};

export const CollectionCollage = (props: CollectionCollageProps) => {
    const { index, maxHeightOffset, content, ratio, imageAlias } = props;
    const { isMobile } = getIsMobile();

    return (
        <PostMediaCover
            key={index}
            ratio={ratio || DEFAULT_POST_COLLECTION_RATIO}
            minWidth="100%"
            isMobile={isMobile}
            maxHeightOffset={maxHeightOffset}
        >
            {generateGrid(content, imageAlias)}
        </PostMediaCover>
    );
};
